import { supabase } from "@/integrations/supabase/client";
import { mapDocumentRow, hydrateLinkedIssues } from "./mappers";
import { COMPLETE_STATES } from "@/domains/documents/utils/documentState";
import {
  SIGNED_CONTAINER_EXTENSIONS,
  isSignedContainerFile,
} from "@/domains/documents/constants/fileTypes";
import type { SortKey, SortOrder } from "@/domains/documents/utils/documentSorting";
import { capture } from "@/lib/sentry/capture";
import { COMPLETE_CONTENT_SEARCH, type ContentSearchStatus } from "./contentSearchStatus";
import type {
  ListParams,
  UploadDateRange,
  PaginatedDocumentsResult,
  DocumentRowMapped,
  DocumentRow,
  DwgChildPdfPreview,
  ProcessingDocument,
} from "./types";

/**
 * Apply ORDER BY clauses to a documents query. Every sort key gets an `id`
 * tie-breaker so equal-key rows land on a deterministic side of any page
 * boundary — otherwise paginated lists can silently duplicate or drop rows
 * when the user's sort column has many equal values. (WHA-3277)
 *
 * For sortKey `modified_at` we order on `documents.effective_modified_at`, a
 * STORED generated column that materialises
 * `COALESCE(document_content_modified_date, original_created_at, created_at)`.
 * That's the exact same expression the "Date Modified" column displays in
 * the UI (see DocumentTableRow.formatDateModified), so the user-visible
 * column always matches the DB sort. Defined in franklin-data-pipeline
 * migration `20260511093235_add_documents_effective_modified_at.sql`.
 *
 * supabase-js returns a fresh builder from each `.order` call, so we thread
 * the chain through rather than mutating in place. The generics are loose —
 * supabase's types differ between FilterBuilder and QueryBuilder.
 */
function applyDocumentsOrder<T extends { order: (...args: any[]) => T }>(
  query: T,
  sortKey: SortKey | undefined,
  sortOrder: SortOrder | undefined
): T {
  const ascending = sortOrder === "asc";
  let ordered: T;
  if (sortKey === "modified_at") {
    ordered = query.order("effective_modified_at", { ascending, nullsFirst: false });
  } else if (sortKey === "name") {
    ordered = query.order("filename", { ascending });
  } else if (sortKey === "size") {
    ordered = query.order("size", { ascending, nullsFirst: false });
  } else if (sortKey === "user") {
    // Sort on the denormalised `created_by_name` column maintained by triggers
    // in franklin-data-pipeline migration 20260511104613_add_documents_
    // created_by_name.sql. PostgREST can't sort outer rows by joined columns,
    // so this denormalised mirror of `user_profiles.first_name + last_name`
    // is the only way to get cross-page alphabetical ordering at the DB
    // level. (WHA-3282)
    ordered = query.order("created_by_name", { ascending, nullsFirst: false });
  } else {
    // Default and explicit `created_at`.
    ordered = query.order("created_at", { ascending, nullsFirst: false });
  }
  // Deterministic tie-breaker for every sort path.
  return ordered.order("id", { ascending });
}

/**
 * Paginated fetch of document IDs from an M2M junction table.
 * Handles PostgREST's max_rows = 1000 limit by fetching in pages.
 *
 * `scope` is mandatory: it narrows to one building (and optionally one project)
 * through an inner embed on `documents`. Unscoped, the sweep pages every
 * junction row the org can see under RLS — for a widely-used tag that is
 * thousands of rows the caller then throws away, several round-trips deep.
 *
 * @param table - Junction table name (e.g. "document_document_categories")
 * @param filterColumn - Column to filter on (e.g. "category_id")
 * @param filterIds - IDs to match against filterColumn
 * @param scope - Building / project the caller cares about
 * @returns All matching document_id values across all pages
 */
async function fetchAllM2MDocumentIds(
  table: string,
  filterColumn: string,
  filterIds: number[],
  scope: { buildingId: number; projectId?: number }
): Promise<number[]> {
  const PAGE_SIZE = 1000;
  const allIds: number[] = [];
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    let q = supabase
      .from(table)
      .select("document_id, documents!inner(building_id, project_id)")
      .in(filterColumn, filterIds)
      .eq("documents.building_id", scope.buildingId);

    if (scope.projectId != null) q = q.eq("documents.project_id", scope.projectId);

    const { data, error } = await q.range(offset, offset + PAGE_SIZE - 1);

    if (error) throw new Error(error.message);
    const page = data ?? [];
    allIds.push(...page.map((r: { document_id: number }) => r.document_id));

    if (page.length < PAGE_SIZE) {
      hasMore = false;
    } else {
      offset += PAGE_SIZE;
    }
  }

  return allIds;
}

/**
 * Ids of the signed containers (.asice/.bdoc/.ddoc) the pipeline has unzipped in
 * this building — the only containers that own enclosed child rows. A container
 * signed inside Franklin is stored terminal with `do_not_unzip`, so it has no
 * children and records its contents in `signed_container_sources` instead.
 *
 * The List view uses this twice: to keep those containers visible (the generic
 * rule hides every UNZIPPED container in favour of its children) and to drop the
 * children themselves, because a signed container is a sealed file whose
 * contents belong in the signature panel, not in the document list.
 *
 * ponytail: an id list per building — tens today. If a building ever nears the
 * cap, move the rule into a DB view or RPC where the parent's filename can be
 * joined instead of enumerated.
 */
// The ids are interpolated into two `or=` filters on one GET alongside a large
// `select`, so a few hundred already risk a 414 at the proxy — the cap sits
// well below that (and below PostgREST's silent 1000-row `max-rows` truncation,
// which would let enclosed children past the cap reappear as list rows). At the
// cap the exception is skipped entirely and logged: signed containers degrade
// to the generic unzipped-container rule (hidden, children listed) instead of
// taking the whole documents list down.
const SIGNED_CONTAINER_ID_LIMIT = 200;

export async function fetchUnzippedSignedContainerIds(buildingId: number): Promise<number[]> {
  const { data, error } = await supabase
    .from("documents")
    .select("id")
    .eq("building_id", buildingId)
    .eq("state", "UNZIPPED")
    .or(SIGNED_CONTAINER_EXTENSIONS.map((ext) => `filename.ilike.%.${ext}`).join(","))
    .limit(SIGNED_CONTAINER_ID_LIMIT);

  // An auxiliary, cosmetic lookup must never take the caller's main query down
  // with it — degrade to the generic unzipped-container rule, same as the cap.
  if (error) {
    console.error("Signed-container id lookup failed:", error.message);
    return [];
  }
  const ids = (data ?? []).map((row: { id: number }) => row.id);
  if (ids.length === SIGNED_CONTAINER_ID_LIMIT) {
    console.error(
      `Building ${buildingId} holds ${SIGNED_CONTAINER_ID_LIMIT}+ unzipped signed containers — ` +
        "skipping the signed-container list exception; move the rule into a DB view/RPC."
    );
    return [];
  }
  return ids;
}

/** `created_at` within an added-date range: from inclusive, to exclusive. */
export function applyUploadDateRange<
  T extends { gte: (column: string, value: string) => T; lt: (column: string, value: string) => T },
>(query: T, range: UploadDateRange | undefined): T {
  let next = query;
  if (range?.from) next = next.gte("created_at", range.from);
  if (range?.to) next = next.lt("created_at", range.to);
  return next;
}

/**
 * The two filters that make a signed container the row and its enclosed
 * documents not-rows, layered onto the generic unzipped-container rule.
 * `signedContainerIds` comes from `fetchUnzippedSignedContainerIds`; every
 * query that decides row eligibility or counts rows must apply this so lists,
 * counts and search agree on the same set.
 */
export function applySignedContainerRowRule<T extends { or: (filters: string) => T }>(
  query: T,
  signedContainerIds: number[]
): T {
  const idList = signedContainerIds.join(",");
  // Container documents: show during processing (CONTAINER, UNZIPPING) but hide
  // once UNZIPPED (their extracted children are visible instead). Normal docs
  // always show (container_type IS NULL). Signed containers are the exception:
  // they stay visible as the row, sealed...
  let q = query.or(
    idList
      ? `container_type.is.null,state.neq.UNZIPPED,id.in.(${idList})`
      : "container_type.is.null,state.neq.UNZIPPED"
  );
  // ...and the documents sealed inside them are not rows at all — they are
  // listed in the signature panel the container row opens.
  if (idList) {
    q = q.or(`parent_document_id.is.null,parent_document_id.not.in.(${idList})`);
  }
  return q;
}

/**
 * Which of these parent ids are signed containers. Children of those are panel
 * content, not rows — used to drop them from result sets built from bare id
 * lists (content search) and from the realtime merge, where the building-wide
 * id-list rule above can't be expressed. Degrades to "none" on error: a
 * signature lookup must never take a document load down with it.
 */
export async function filterSignedContainerParentIds(parentIds: number[]): Promise<Set<number>> {
  const unique = [...new Set(parentIds)];
  if (unique.length === 0) return new Set();

  const { data, error } = await supabase.from("documents").select("id, filename").in("id", unique);

  if (error) {
    console.error("Signed-container parent lookup failed:", error.message);
    return new Set();
  }
  return new Set(
    (data ?? [])
      .filter((row: { id: number; filename: string }) => isSignedContainerFile(row.filename))
      .map((row: { id: number }) => row.id)
  );
}

/**
 * Fetch a list of documents for a building with user name join.
 * Returns essential fields for list display with pagination support.
 *
 * All filters are applied at the SQL query level BEFORE pagination (LIMIT/OFFSET),
 * ensuring correct results when filtering and paginating together.
 */
export async function listDocuments(params: ListParams): Promise<PaginatedDocumentsResult> {
  const {
    buildingId,
    limit = 50,
    offset = 0,
    states,
    excludeStates,
    projectIds,
    categoryIds,
    designDisciplineIds,
    designStageIds,
    searchText,
    folderId,
    documentIds,
    uploadDateRange,
    verifiedOnly,
    sortKey,
    sortOrder,
  } = params;
  const numericBuildingId = Number(buildingId);
  // An empty id restriction names no documents — never "no restriction".
  if (documentIds?.length === 0) return { data: [], totalCount: 0, hasMore: false };

  // Signed containers are rows in their own right — see the helper. Resolved
  // before the main query because both the visibility exception and the
  // enclosed-children exclusion below are expressed as id filters.
  const signedContainerIds = await fetchUnzippedSignedContainerIds(numericBuildingId);

  // Tag dimensions (categories / disciplines / stages) are filtered by the
  // DATABASE, as one predicate on the main query, via an aliased inner embed of
  // each junction table:
  //
  //   cat_filter:document_document_categories!inner(category_id)
  //   &cat_filter.category_id=in.(…)
  //
  // PostgREST compiles that to an inner join on an aggregated lateral, so a row
  // matching several selected ids still comes back ONCE, `count: exact` counts
  // parent rows, and LIMIT/OFFSET/ORDER stay server-side. Aliases are required
  // because the select below also embeds these same three tables UNFILTERED, to
  // render each row's full set of tag chips.
  //
  // This replaces a client-side pre-pass that paged every junction row in the
  // ORG (no building scope), once per dimension, sequentially, and then posted
  // the intersection back as one `id=in.(…)` — a URL that grew past the proxy's
  // limit on any widely-used tag and stalled the whole list.
  const tagFilters: Array<{ embed: string; column: string; ids: number[] }> = [];
  if (categoryIds && categoryIds.length > 0) {
    tagFilters.push({
      embed: "cat_filter:document_document_categories!inner(category_id)",
      column: "cat_filter.category_id",
      ids: categoryIds,
    });
  }
  if (designDisciplineIds && designDisciplineIds.length > 0) {
    tagFilters.push({
      embed: "disc_filter:document_design_disciplines!inner(discipline_id)",
      column: "disc_filter.discipline_id",
      ids: designDisciplineIds,
    });
  }
  if (designStageIds && designStageIds.length > 0) {
    tagFilters.push({
      embed: "stage_filter:document_design_stages!inner(stage_id)",
      column: "stage_filter.stage_id",
      ids: designStageIds,
    });
  }
  const tagFilterSelect = tagFilters.map((f) => `,\n      ${f.embed}`).join("");

  let query = supabase
    .from("documents")
    .select(
      `
      id,
      filename,
      file_path,
      building_id,
      parent_document_id,
      content_type,
      size,
      created_at,
      original_created_at,
      modified_at,
      document_content_modified_date,
      effective_modified_at,
      created_by,
      state,
      version,
      version_group,
      source,
      container_type,
      project_id,
      folder_id,
      is_deleted,
      approved,
      verified_at,
      verified_by,
      subcategories,
      project_stage,
      user_profiles!documents_created_by_fk (
        first_name,
        last_name,
        avatar_url,
        color
      ),
      verified_by_profile:user_profiles!documents_verified_by_fk (
        first_name,
        last_name
      ),
      document_document_categories(
        document_categories(id, category_name, name_et)
      ),
      document_design_disciplines(
        design_disciplines(id, name, name_en, abbreviation, description)
      ),
      document_design_stages(
        design_stages(id, name, name_en, abbreviation)
      )${tagFilterSelect}
    `,
      { count: "exact" }
    )
    .eq("building_id", numericBuildingId)
    .eq("is_deleted", false)
    .eq("is_latest_version", true)
    // WHA-3330: exclude chatbot-attachment + Issue Attachments docs.
    .eq("hidden", false)
    // Exclude DWG child documents (converted PDFs from DWG files)
    .or("is_dwg_child.is.null,is_dwg_child.eq.false");

  query = applySignedContainerRowRule(query, signedContainerIds);

  // AND across dimensions, OR within one — see the embed comment above.
  for (const { column, ids } of tagFilters) {
    query = query.in(column, ids);
  }

  // State filters
  if (states && states.length > 0) {
    query = query.in("state", states);
  }
  if (excludeStates && excludeStates.length > 0) {
    query = query.not("state", "in", `(${excludeStates.join(",")})`);
  }

  // Project IDs filter
  if (projectIds && projectIds.length > 0) {
    query = query.in("project_id", projectIds);
  }

  // Search text filter - split on spaces so each term is matched independently (AND logic)
  // e.g. "floor plan pdf" matches filenames containing all three terms
  //
  // Unicode normalization: Filenames may be stored in NFC (precomposed, e.g. õ = U+00F5)
  // or NFD (decomposed, e.g. o + combining tilde). Browsers send NFC by default.
  // We search with both forms so matches work regardless of how the filename was stored.
  if (searchText && searchText.trim()) {
    const terms = searchText.trim().split(/\s+/);
    for (const term of terms) {
      const nfc = term.normalize("NFC");
      const nfd = term.normalize("NFD");
      if (nfc === nfd) {
        // No Unicode characters affected by normalization — single filter suffices
        query = query.ilike("filename", `%${term}%`);
      } else {
        // Search with both normalizations using OR
        query = query.or(`filename.ilike.%${nfc}%,filename.ilike.%${nfd}%`);
      }
    }
  }

  // Folder ID filter
  if (folderId !== undefined) {
    if (folderId === null) {
      query = query.is("folder_id", null);
    } else {
      query = query.eq("folder_id", Number(folderId));
    }
  }

  if (documentIds) {
    query = query.in("id", documentIds);
  }

  query = applyUploadDateRange(query, uploadDateRange);

  // Verified filter. `verified_at IS NOT NULL` is the boolean — there is no
  // separate flag column.
  if (verifiedOnly) {
    query = query.not("verified_at", "is", null);
  }

  // Apply sorting and pagination AFTER all filters.
  // The ORDER BY for `modified_at` chains three columns so the NULL tail of
  // `document_content_modified_date` has a deterministic secondary key —
  // without it pagination across the NULL bucket was non-contiguous and the
  // FE re-sort made each page look "sorted within itself but not across".
  // (WHA-3277)
  const { data, count, error } = await applyDocumentsOrder(query, sortKey, sortOrder).range(
    offset,
    offset + limit - 1
  );

  if (error) throw new Error(error.message);

  const totalCount = count ?? 0;
  const returnedCount = data?.length ?? 0;

  const mapped = (data ?? []).map(mapDocumentRow);
  // WHA-3270 — batch-hydrate the PM issue refs for the chip beside the
  // preview icon. Graceful on failure (hides the chip, not the row).
  await hydrateLinkedIssues(mapped);

  return {
    data: mapped,
    totalCount,
    hasMore: totalCount > offset + returnedCount,
  };
}

/**
 * Get a single document by ID.
 */
export async function getDocumentById(id: string): Promise<DocumentRow | null> {
  const numericId = Number(id);
  const { data, error } = await supabase
    .from("documents")
    .select("*")
    .eq("id", numericId)
    .maybeSingle();

  if (error) throw new Error(error.message);
  return (data as DocumentRow) ?? null;
}

/**
 * Get a document by exact filename within a building.
 * Used for chatbot document links to open the preview viewer.
 * Returns the most recently created document if multiple exist with the same filename.
 */
export async function getDocumentByFilename(
  buildingId: string,
  filename: string
): Promise<DocumentRowMapped | null> {
  const numericBuildingId = Number(buildingId);

  const { data, error } = await supabase
    .from("documents")
    .select(
      `
      id,
      filename,
      file_path,
      building_id,
      parent_document_id,
      content_type,
      size,
      created_at,
      original_created_at,
      modified_at,
      document_content_modified_date,
      effective_modified_at,
      created_by,
      state,
      version,
      version_group,
      source,
      container_type,
      project_id,
      folder_id,
      is_deleted,
      approved,
      verified_at,
      verified_by,
      user_profiles!documents_created_by_fk (
        first_name,
        last_name,
        avatar_url,
        color
      ),
      verified_by_profile:user_profiles!documents_verified_by_fk (
        first_name,
        last_name
      ),
      document_document_categories(
        document_categories(id, category_name, name_et)
      ),
      document_design_disciplines(
        design_disciplines(id, name, name_en, abbreviation, description)
      ),
      document_design_stages(
        design_stages(id, name, name_en, abbreviation)
      )
    `
    )
    .eq("building_id", numericBuildingId)
    .eq("filename", filename)
    .eq("is_deleted", false)
    .eq("is_latest_version", true)
    // Exclude DWG child documents (converted PDFs from DWG files)
    .or("is_dwg_child.is.null,is_dwg_child.eq.false")
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw new Error(error.message);
  if (!data) return null;

  return mapDocumentRow(data);
}

/**
 * Validate a candidate filename list against visible building documents.
 * Returns exact filenames that exist in the building and can be previewed.
 */
export async function getExistingDocumentFilenames(
  buildingId: string,
  filenames: string[]
): Promise<string[]> {
  const numericBuildingId = Number(buildingId);
  const uniqueFilenames = Array.from(
    new Set(filenames.map((name) => name.trim()).filter((name) => Boolean(name)))
  );

  if (uniqueFilenames.length === 0) return [];

  const { data, error } = await supabase
    .from("documents")
    .select("filename")
    .eq("building_id", numericBuildingId)
    .in("filename", uniqueFilenames)
    .eq("is_deleted", false)
    .eq("is_latest_version", true)
    .eq("hidden", false)
    // Exclude container children (files inside .asice, .zip, etc.)
    .is("parent_document_id", null)
    // Exclude DWG child documents (converted PDFs from DWG files)
    .or("is_dwg_child.is.null,is_dwg_child.eq.false");

  if (error) throw new Error(error.message);
  return (data ?? []).map((d: { filename: string }) => d.filename);
}

/**
 * Fetch a single document with full relations (for adding to store after realtime update).
 */
export async function fetchSingleDocument(docId: number): Promise<DocumentRowMapped | null> {
  const { data, error } = await supabase
    .from("documents")
    .select(
      `
      id,
      filename,
      file_path,
      building_id,
      parent_document_id,
      content_type,
      size,
      created_at,
      original_created_at,
      modified_at,
      document_content_modified_date,
      effective_modified_at,
      created_by,
      state,
      version,
      version_group,
      source,
      container_type,
      is_latest_version,
      project_id,
      folder_id,
      is_deleted,
      approved,
      verified_at,
      verified_by,
      user_profiles!documents_created_by_fk (
        first_name,
        last_name,
        avatar_url,
        color
      ),
      verified_by_profile:user_profiles!documents_verified_by_fk (
        first_name,
        last_name
      ),
      document_document_categories(
        document_categories(id, category_name, name_et)
      ),
      document_design_disciplines(
        design_disciplines(id, name, name_en, abbreviation, description)
      ),
      document_design_stages(
        design_stages(id, name, name_en, abbreviation)
      )
    `
    )
    .eq("id", docId)
    // Exclude DWG child documents (converted PDFs from DWG files)
    .or("is_dwg_child.is.null,is_dwg_child.eq.false")
    .maybeSingle();

  if (error) throw new Error(error.message);
  if (!data) return null;

  return mapDocumentRow(data);
}

/**
 * Fetch child documents for a parent document (container files like .asice, .zip).
 */
export async function fetchChildDocuments(parentDocumentId: string): Promise<DocumentRowMapped[]> {
  const numericParentId = Number(parentDocumentId);

  const { data, error } = await supabase
    .from("documents")
    .select(
      `
      id,
      filename,
      file_path,
      building_id,
      parent_document_id,
      content_type,
      size,
      created_at,
      original_created_at,
      modified_at,
      document_content_modified_date,
      effective_modified_at,
      created_by,
      state,
      version,
      version_group,
      source,
      container_type,
      project_id,
      folder_id,
      is_deleted,
      approved,
      verified_at,
      verified_by,
      user_profiles!documents_created_by_fk (
        first_name,
        last_name,
        avatar_url,
        color
      ),
      verified_by_profile:user_profiles!documents_verified_by_fk (
        first_name,
        last_name
      ),
      document_document_categories(
        document_categories(id, category_name, name_et)
      ),
      document_design_disciplines(
        design_disciplines(id, name, name_en, abbreviation, description)
      ),
      document_design_stages(
        design_stages(id, name, name_en, abbreviation)
      )
    `
    )
    .eq("parent_document_id", numericParentId)
    .eq("is_deleted", false)
    .order("filename", { ascending: true });

  if (error) throw new Error(error.message);

  return (data ?? []).map(mapDocumentRow);
}

export interface FetchProjectDocumentsParams {
  projectId: string;
  /** null = root documents (no folder), undefined = all docs, string = specific folder */
  folderId?: string | null;
  limit?: number;
  offset?: number;
  /**
   * Optional column the SQL `.order()` clause should sort by. When omitted the
   * server falls back to `created_at desc` (preserves the historical default
   * for callers that don't pass a sort, e.g. `peekFolderDocuments`).
   */
  sortKey?: SortKey;
  sortOrder?: SortOrder;
}

/**
 * What makes a `documents` row a row in the project browse views (Folders and
 * the folder-scoped List): the newest version, not trashed, not hidden (chat
 * attachments live in a hidden project), and neither a container child nor a
 * DWG rendition — both of those are reached through their parent, never listed
 * beside it.
 *
 * Anything that counts or gates on "the files in this folder" must apply this
 * rather than re-deriving it, or the number stops matching the rows on screen
 * (WHA-3654: the organise modal's scope counts).
 */
export function applyVisibleDocumentFilters<
  T extends {
    eq(column: string, value: boolean): T;
    is(column: string, value: null): T;
    or(filters: string): T;
  },
>(query: T): T {
  return query
    .eq("is_deleted", false)
    .eq("is_latest_version", true)
    .eq("hidden", false)
    .is("parent_document_id", null)
    .or("is_dwg_child.is.null,is_dwg_child.eq.false");
}

/**
 * Fetch documents for a project with folder navigation support.
 */
export async function fetchProjectDocuments(
  params: FetchProjectDocumentsParams
): Promise<DocumentRowMapped[]> {
  const { projectId, folderId, limit = 100, offset = 0, sortKey, sortOrder } = params;
  const numericProjectId = Number(projectId);

  // For specific folder: query documents directly by folder_id
  if (folderId !== null && folderId !== undefined) {
    const numericFolderId = Number(folderId);

    const folderQuery = supabase
      .from("documents")
      .select(
        `
        id,
        filename,
        file_path,
        building_id,
        parent_document_id,
        content_type,
        size,
        created_at,
        original_created_at,
        modified_at,
        document_content_modified_date,
        effective_modified_at,
        created_by,
        state,
        version,
        version_group,
        source,
      container_type,
        project_id,
        folder_id,
        is_deleted,
        approved,
        verified_at,
        verified_by,
        subcategories,
        project_stage,
        user_profiles!documents_created_by_fk (
          first_name,
          last_name,
          avatar_url,
          color
        ),
        verified_by_profile:user_profiles!documents_verified_by_fk (
          first_name,
          last_name
        ),
        document_document_categories(
          document_categories(id, category_name, name_et)
        ),
        document_design_disciplines(
          design_disciplines(id, name, name_en, abbreviation, description)
        ),
        document_design_stages(
          design_stages(id, name, name_en, abbreviation)
        )
      `
      )
      .eq("project_id", numericProjectId)
      .eq("folder_id", numericFolderId);
    const baseQuery = applyVisibleDocumentFilters(folderQuery);

    const { data, error } = await applyDocumentsOrder(baseQuery, sortKey, sortOrder).range(
      offset,
      offset + limit - 1
    );

    if (error) throw new Error(error.message);
    const mapped = (data ?? []).map(mapDocumentRow);
    // WHA-3270: hydrate the LinkedIssuesChip data — was missing on /projects
    // because this folder-scoped branch wasn't patched.
    await hydrateLinkedIssues(mapped);
    return mapped;
  }

  // For root documents (folderId === null): docs with no folder
  if (folderId === null) {
    const rootQuery = supabase
      .from("documents")
      .select(
        `
        id,
        filename,
        file_path,
        building_id,
        parent_document_id,
        content_type,
        size,
        created_at,
        original_created_at,
        modified_at,
        document_content_modified_date,
        effective_modified_at,
        created_by,
        state,
        version,
        version_group,
        source,
      container_type,
        project_id,
        folder_id,
        is_deleted,
        approved,
        verified_at,
        verified_by,
        subcategories,
        project_stage,
        user_profiles!documents_created_by_fk (
          first_name,
          last_name,
          avatar_url,
          color
        ),
        verified_by_profile:user_profiles!documents_verified_by_fk (
          first_name,
          last_name
        ),
        document_document_categories(
          document_categories(id, category_name, name_et)
        ),
        document_design_disciplines(
          design_disciplines(id, name, name_en, abbreviation, description)
        ),
        document_design_stages(
          design_stages(id, name, name_en, abbreviation)
        )
      `
      )
      .eq("project_id", numericProjectId)
      .is("folder_id", null);
    const baseQuery = applyVisibleDocumentFilters(rootQuery);

    const { data, error } = await applyDocumentsOrder(baseQuery, sortKey, sortOrder).range(
      offset,
      offset + limit - 1
    );

    if (error) throw new Error(error.message);
    const mapped = (data ?? []).map(mapDocumentRow);
    // WHA-3270: hydrate the LinkedIssuesChip data — same as the
    // folder-scoped branch above.
    await hydrateLinkedIssues(mapped);
    return mapped;
  }

  // folderId is undefined: return ALL documents in project
  const allQuery = supabase
    .from("documents")
    .select(
      `
      id,
      filename,
      file_path,
      building_id,
      parent_document_id,
      content_type,
      size,
      created_at,
      original_created_at,
      modified_at,
      document_content_modified_date,
      effective_modified_at,
      created_by,
      state,
      version,
      version_group,
      source,
      container_type,
      project_id,
      folder_id,
      is_deleted,
      approved,
      verified_at,
      verified_by,
      subcategories,
      project_stage,
      user_profiles!documents_created_by_fk (
        first_name,
        last_name,
        avatar_url,
        color
      ),
      verified_by_profile:user_profiles!documents_verified_by_fk (
        first_name,
        last_name
      ),
      document_document_categories(
        document_categories(id, category_name, name_et)
      ),
      document_design_disciplines(
        design_disciplines(id, name, name_en, abbreviation, description)
      ),
      document_design_stages(
        design_stages(id, name, name_en, abbreviation)
      )
    `
    )
    .eq("project_id", numericProjectId);
  const baseQuery = applyVisibleDocumentFilters(allQuery);

  const { data, error } = await applyDocumentsOrder(baseQuery, sortKey, sortOrder).range(
    offset,
    offset + limit - 1
  );

  if (error) throw new Error(error.message);
  const mapped = (data ?? []).map(mapDocumentRow);
  // WHA-3270 — same chip hydration the documents list does.
  await hydrateLinkedIssues(mapped);
  return mapped;
}

/**
 * Fetch child document counts for multiple container files (.zip, .asice).
 * Returns a Map of parent document ID (string) -> child count.
 * Used by the project view to show child counts on container folder rows.
 */
export async function fetchContainerChildCounts(
  parentDocumentIds: string[]
): Promise<Map<string, number>> {
  if (parentDocumentIds.length === 0) {
    return new Map();
  }

  const numericIds = parentDocumentIds.map(Number);

  const { data, error } = await supabase
    .from("documents")
    .select("parent_document_id")
    .in("parent_document_id", numericIds)
    .eq("is_deleted", false);

  if (error) throw new Error(error.message);

  // Count per parent in JS
  const counts = new Map<string, number>();
  for (const row of data ?? []) {
    const parentId = String(row.parent_document_id);
    counts.set(parentId, (counts.get(parentId) ?? 0) + 1);
  }

  return counts;
}

/**
 * Container file extensions used to identify ZIP/archive documents.
 */
const CONTAINER_EXTENSIONS = [".zip", ".asice", ".bdoc", ".ddoc"];

/**
 * Fetch all container documents (.zip, .asice) for a project with child counts.
 * Used by the folder tree to inject virtual container entries.
 */
export async function fetchProjectContainers(
  projectId: string
): Promise<{ id: string; filename: string; folderId: string | null; childCount: number }[]> {
  const numericProjectId = Number(projectId);

  // Fetch container documents filtered at DB level
  const { data, error } = await supabase
    .from("documents")
    .select("id, filename, folder_id")
    .eq("project_id", numericProjectId)
    .eq("is_deleted", false)
    .eq("is_latest_version", true)
    .is("parent_document_id", null)
    .or(CONTAINER_EXTENSIONS.map((ext) => `filename.ilike.%${ext}`).join(","));

  if (error) throw new Error(error.message);

  const containers = data ?? [];

  if (containers.length === 0) return [];

  // Fetch child counts in batch
  const containerIds = containers.map((c: { id: number }) => c.id);
  const childCounts = await fetchContainerChildCounts(containerIds.map(String));

  return containers.map((c: { id: number; filename: string; folder_id: number | null }) => ({
    id: String(c.id),
    filename: c.filename,
    folderId: c.folder_id != null ? String(c.folder_id) : null,
    childCount: childCounts.get(String(c.id)) ?? 0,
  }));
}

/**
 * Fetch child PDF metadata for DWG file preview.
 * Returns lightweight data (file_path, content_type) for the child PDF.
 * Uses .maybeSingle() for safer error handling when child may not exist.
 */
export async function fetchDwgChildPdfForPreview(
  parentDocId: string
): Promise<DwgChildPdfPreview | null> {
  const numericParentId = Number(parentDocId);

  if (isNaN(numericParentId)) {
    throw new Error(`Invalid parent document ID: ${parentDocId}`);
  }

  const { data, error } = await supabase
    .from("documents")
    .select("id, file_path, content_type")
    .eq("parent_document_id", numericParentId)
    .maybeSingle();

  if (error) throw new Error(error.message);

  if (!data || !data.file_path) {
    return null;
  }

  return {
    id: data.id,
    file_path: data.file_path,
    content_type: data.content_type,
  };
}

/**
 * Fetch child PDF states for multiple DWG parent documents.
 * Returns a Map of parent document ID (string) -> child document state.
 * Used by useDwgChildStates hook to display child processing status.
 */
export async function fetchDwgChildStates(parentIds: number[]): Promise<Map<string, string>> {
  if (parentIds.length === 0) {
    return new Map();
  }

  const { data, error } = await supabase
    .from("documents")
    .select("id, state, parent_document_id")
    .in("parent_document_id", parentIds);

  if (error) throw new Error(error.message);

  // Build map of parent DWG ID -> child PDF state
  // Note: We key by parent ID so consumers can look up by DWG doc ID
  return new Map(
    (data ?? []).map((d: { parent_document_id: number | null; state: string }) => [
      String(d.parent_document_id),
      d.state,
    ])
  );
}

/**
 * Fetch count of processing documents for a building (documents not yet in terminal state).
 * Uses efficient COUNT query without fetching document data.
 * Used for real-time processing count display in upload bar.
 *
 * `createdSince` narrows the count to documents created at/after that time —
 * used to watch a specific upload batch without older stuck/reprocessing
 * documents keeping the count above zero forever.
 */
export async function fetchProcessingDocumentCount(
  buildingId: string,
  createdSince?: string
): Promise<number> {
  const numericBuildingId = Number(buildingId);

  const signedContainerIds = await fetchUnzippedSignedContainerIds(numericBuildingId);
  let query = supabase
    .from("documents")
    .select("*", { count: "exact", head: true })
    .eq("building_id", numericBuildingId)
    .eq("is_deleted", false)
    .eq("is_latest_version", true)
    .eq("hidden", false)
    .not("state", "in", `(${COMPLETE_STATES.join(",")})`)
    .or("is_dwg_child.is.null,is_dwg_child.eq.false");
  if (createdSince) query = query.gte("created_at", createdSince);
  query = applySignedContainerRowRule(query, signedContainerIds);

  const { count, error } = await query;
  if (error) throw new Error(error.message);
  return count ?? 0;
}

/**
 * Fetch processing document IDs and count for a building.
 * Returns both for deduplication of WebSocket events.
 */
export async function fetchProcessingDocumentIdsAndCount(
  buildingId: string,
  projectId?: string | null
): Promise<{ ids: Set<string>; count: number }> {
  const numericBuildingId = Number(buildingId);
  const numericProjectId = projectId ? Number(projectId) : null;
  const signedContainerIds = await fetchUnzippedSignedContainerIds(numericBuildingId);

  let query = supabase
    .from("documents")
    .select("id", { count: "exact" })
    .eq("building_id", numericBuildingId)
    .eq("is_deleted", false)
    .eq("is_latest_version", true)
    .eq("hidden", false)
    .not("state", "in", `(${COMPLETE_STATES.join(",")})`)
    .or("is_dwg_child.is.null,is_dwg_child.eq.false");
  query = applySignedContainerRowRule(query, signedContainerIds);
  if (numericProjectId != null) query = query.eq("project_id", numericProjectId);

  const { data, count, error } = await query;

  if (error) throw new Error(error.message);

  const ids = new Set<string>((data ?? []).map((d: { id: number }) => String(d.id)));
  return { ids, count: count ?? 0 };
}

/**
 * Get all document filenames for a building (for auto-detection in chat).
 * Returns a list of filenames for documents that are not deleted and not container children.
 * Used by the chatbot to automatically detect and linkify document references.
 */
export async function getDocumentFilenames(buildingId: string): Promise<string[]> {
  const numericBuildingId = Number(buildingId);

  const { data, error } = await supabase
    .from("documents")
    .select("filename")
    .eq("building_id", numericBuildingId)
    .eq("is_deleted", false)
    .eq("is_latest_version", true)
    .eq("hidden", false)
    // Exclude container children (files inside .asice, .zip, etc.)
    .is("parent_document_id", null)
    // Exclude DWG child documents (converted PDFs from DWG files)
    .or("is_dwg_child.is.null,is_dwg_child.eq.false");

  if (error) throw new Error(error.message);
  return (data ?? []).map((d: { filename: string }) => d.filename);
}

/**
 * Recursively collect all descendant folder IDs for given root folder IDs.
 * Walks the document_folders hierarchy one level at a time via parent_id.
 * Returns all IDs including the roots. Throws on a query error — a partial
 * subtree would silently narrow downstream scopes (tag filters, shares,
 * downloads) to a subset of the real matches.
 */
export async function getAllDescendantFolderIds(rootIds: number[]): Promise<number[]> {
  const allIds = [...rootIds];
  let currentLevel = rootIds;
  while (currentLevel.length > 0) {
    const { data: children, error } = await supabase
      .from("document_folders")
      .select("id")
      .in("parent_id", currentLevel);
    if (error) throw new Error(error.message);
    if (!children || children.length === 0) break;
    const childIds = children.map((f: { id: number }) => f.id);
    allIds.push(...childIds);
    currentLevel = childIds;
  }
  return allIds;
}

/**
 * Walk up the document_folders hierarchy from each starting folder ID,
 * collecting every ancestor (and the starting IDs themselves). Used by
 * restore flows to un-soft-delete every folder in a document's path.
 */
export async function getAllAncestorFolderIds(startIds: number[]): Promise<number[]> {
  if (startIds.length === 0) return [];
  const seen = new Set<number>(startIds);
  let currentLevel = startIds;
  while (currentLevel.length > 0) {
    const { data, error } = await supabase
      .from("document_folders")
      .select("parent_id")
      .in("id", currentLevel);
    if (error) {
      console.error("Error fetching parent folders:", error);
      break;
    }
    currentLevel = collectNewParentIds(data ?? [], seen);
  }
  return Array.from(seen);
}

function collectNewParentIds(
  rows: Array<{ parent_id: number | null }>,
  seen: Set<number>
): number[] {
  const next: number[] = [];
  for (const row of rows) {
    const parentId = row.parent_id;
    if (parentId == null) continue;
    if (seen.has(parentId)) continue;
    seen.add(parentId);
    next.push(parentId);
  }
  return next;
}

/**
 * Fetch processing documents for a building (documents not yet in terminal state).
 * Returns documents with user profile info, sorted with active processing first.
 */
export async function fetchProcessingDocuments(buildingId: string): Promise<ProcessingDocument[]> {
  const numericBuildingId = Number(buildingId);

  const { data, error } = await supabase
    .from("documents")
    .select(
      `
      id,
      filename,
      file_path,
      building_id,
      parent_document_id,
      content_type,
      size,
      created_at,
      state,
      version,
      user_profiles!documents_created_by_fk(id,first_name,last_name,avatar_url,color)
    `
    )
    .eq("building_id", numericBuildingId)
    .eq("is_deleted", false)
    .eq("is_latest_version", true)
    .eq("hidden", false)
    .not("state", "in", `(${COMPLETE_STATES.join(",")})`)
    // Exclude DWG child documents (converted PDFs from DWG files)
    .or("is_dwg_child.is.null,is_dwg_child.eq.false")
    .order("created_at", { ascending: false })
    .limit(100);

  if (error) throw new Error(error.message);

  // Map to ProcessingDocument type
  const mapped: ProcessingDocument[] = (data ?? []).map((doc: any) => ({
    id: String(doc.id),
    filename: doc.filename,
    file_path: doc.file_path,
    building_id: String(doc.building_id),
    parent_document_id: doc.parent_document_id ? String(doc.parent_document_id) : null,
    content_type: doc.content_type,
    size: doc.size,
    created_at: doc.created_at,
    state: doc.state,
    version: doc.version,
    created_by_name:
      doc.user_profiles?.first_name || doc.user_profiles?.last_name
        ? [doc.user_profiles.first_name, doc.user_profiles.last_name].filter(Boolean).join(" ")
        : null,
    created_by_avatar_url: doc.user_profiles?.avatar_url ?? null,
    created_by_color: doc.user_profiles?.color ?? null,
  }));

  // Sort: UNPROCESSED/null states at bottom, active processing at top (by created_at desc)
  return mapped.sort((a, b) => {
    const aIsUnprocessed = !a.state || a.state === "UNPROCESSED";
    const bIsUnprocessed = !b.state || b.state === "UNPROCESSED";

    // If one is unprocessed and the other isn't, unprocessed goes to bottom
    if (aIsUnprocessed && !bIsUnprocessed) return 1;
    if (!aIsUnprocessed && bIsUnprocessed) return -1;

    // Otherwise sort by created_at descending (newest first)
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });
}

/**
 * Search document content via the search_documents_by_content RPC.
 *
 * Returns a Map keyed by document ID. The RPC returns content-only matches
 * (filename overlap is excluded server-side), so callers can append these
 * results to filename-search output without dedup (WHA-3179). Each entry
 * carries the location of the first matching page/sheet plus the total
 * match count, used by the Content chip tooltip ("Found on page 3 (+4 more)").
 *
 * `scope` narrows the search:
 *   - `projectId`: restrict to one project (avoids cross-project leak on /projects).
 *   - `folderIds`: restrict to a set of folder_ids — caller passes the current
 *     folder's subtree (use `getAllDescendantFolderIds` to expand). NULL means
 *     "no folder filter" (whole project / building).
 */
export interface ContentMatchInfo {
  firstMatchPage: number | null;
  sheetName: string | null;
  matchCount: number;
}

export interface SearchDocumentContentScope {
  projectId?: number | null;
  folderIds?: number[] | null;
}

/** The RPC's page size: a full page means more matches may exist beyond it. */
const CONTENT_SEARCH_LIMIT = 200;

export async function searchDocumentContentWithStatus(
  buildingId: string,
  searchText: string,
  scope?: SearchDocumentContentScope
): Promise<{ matches: Map<number, ContentMatchInfo>; status: ContentSearchStatus }> {
  const trimmed = searchText.trim();
  if (!trimmed) return { matches: new Map(), status: COMPLETE_CONTENT_SEARCH };

  const { data, error } = await supabase.rpc("search_documents_by_content", {
    p_building_id: Number(buildingId),
    p_search_text: trimmed,
    p_limit: CONTENT_SEARCH_LIMIT,
    p_project_id: scope?.projectId ?? null,
    p_folder_ids: scope?.folderIds ?? null,
  });

  if (error) {
    // Fail-soft for the views (filename matches still show, with a notice),
    // but never silently: a broken text search otherwise reads as "no matches".
    capture(new Error(`Document content search failed: ${error.message}`), {
      tags: { domain: "documents", operation: "content_search" },
      fingerprint: ["documents", "content-search-unavailable"],
      context: { search: { buildingId } },
    });
    return { matches: new Map(), status: { isPartial: false, contentUnavailable: true } };
  }

  const rows = (data ?? []) as Array<{
    document_id: number;
    page_number: number | null;
    sheet_name: string | null;
    match_count: number;
  }>;
  const matches = new Map<number, ContentMatchInfo>();
  for (const row of rows) {
    matches.set(row.document_id, {
      firstMatchPage: row.page_number,
      sheetName: row.sheet_name,
      matchCount: row.match_count,
    });
  }
  return {
    matches,
    status: { isPartial: rows.length >= CONTENT_SEARCH_LIMIT, contentUnavailable: false },
  };
}

export async function searchDocumentContent(
  buildingId: string,
  searchText: string,
  scope?: SearchDocumentContentScope
): Promise<Map<number, ContentMatchInfo>> {
  return (await searchDocumentContentWithStatus(buildingId, searchText, scope)).matches;
}

/**
 * Subtree filename search for /projects.
 *
 * Returns documents whose filename matches `searchText` AND that live in the
 * given project + folder subtree. Mirrors the visibility filters of
 * search_documents_by_content (latest version, exclude UNZIPPED containers,
 * exclude DWG child PDFs) so the two search paths return the same set of
 * eligible rows, plus the two the browse lists apply and it does not —
 * `hidden = false` and `parent_document_id IS NULL`. Without those, typing a
 * filter offers rows no view lists while browsing: hidden documents (a chat
 * attachment's project) and the files extracted out of a container. Same pair
 * as the tag-filtered subtree fetch below.
 *
 * `folderIds`:
 *   - `null` — no folder constraint (whole project).
 *   - `[]` — empty array, returns no rows (defensive).
 *   - non-empty — restrict to documents whose `folder_id` is in the array.
 *
 * Implementation note: the query lands the matching IDs server-side, then
 * delegates to fetchDocumentsByIds for the full row shape. Two round-trips,
 * but reuses the canonical select + mapper path so column drift can't
 * silently diverge. A long `folderIds` is chunked (IN_CHUNK_SIZE) for the same
 * reason fetchDocumentsByIds chunks its ids — PostgREST encodes `.in()` in the
 * request URL.
 */
export async function searchProjectFilenameMatches(params: {
  buildingId: string;
  projectId: number;
  folderIds: number[] | null;
  searchText: string;
  limit?: number;
  /**
   * Surface a failed search to the caller instead of degrading to the matches
   * collected so far. A view that merges these rows into ones it already shows
   * (the Folders tab) wants the degrade; one whose whole result IS this list
   * (the document picker's filter) would otherwise render a partial or empty
   * answer as "nothing matches".
   */
  throwOnError?: boolean;
}): Promise<{ documents: DocumentRowMapped[]; truncated: boolean }> {
  const trimmed = params.searchText.trim();
  if (!trimmed) return { documents: [], truncated: false };
  if (params.folderIds !== null && params.folderIds.length === 0) {
    return { documents: [], truncated: false };
  }

  // The container is the only row for its contents, so it must be findable —
  // and the enclosed documents must not leak into results the list never shows.
  const signedContainerIds = await fetchUnzippedSignedContainerIds(Number(params.buildingId));

  // Chunk the folder filter (see IN_CHUNK_SIZE): PostgREST puts an `.in()` list
  // in the request URL, so a folder with a few hundred descendants overflows it
  // and the request fails — which a `throwOnError` caller (the picker) surfaces
  // as "couldn't search" instead of the matches that do exist.
  const limit = params.limit ?? 200;
  const folderIds = params.folderIds;
  const folderChunks: Array<number[] | null> =
    folderIds === null
      ? [null]
      : Array.from({ length: Math.ceil(folderIds.length / IN_CHUNK_SIZE) }, (_, i) =>
          folderIds.slice(i * IN_CHUNK_SIZE, (i + 1) * IN_CHUNK_SIZE)
        );

  const ids: number[] = [];
  for (const chunk of folderChunks) {
    let q = supabase
      .from("documents")
      .select("id")
      .eq("building_id", Number(params.buildingId))
      .eq("project_id", params.projectId)
      .eq("is_deleted", false)
      .eq("is_latest_version", true)
      .eq("hidden", false)
      .is("parent_document_id", null)
      .or("is_dwg_child.is.null,is_dwg_child.eq.false")
      .ilike("filename", `%${trimmed}%`)
      // Newest-first, and the FULL cap on every chunk — not the remainder. A
      // chunk only ever sees its own folders, so filling the cap from the first
      // one and stopping would leave later folders unqueried, hiding matches
      // NEWER than everything returned while the caller's "first N matches"
      // banner presents the result as the newest of the scope. The cap is
      // applied to the merged ids below, once every chunk has been seen.
      //
      // One id beyond the cap, so `truncated` can tell "exactly `limit`
      // matches, all of them here" from "more than we return". A chunk that
      // came back under `limit + 1` was not cut off, so merged ids <= limit
      // means nothing was hidden anywhere.
      .order("id", { ascending: false })
      .limit(limit + 1);
    q = applySignedContainerRowRule(q, signedContainerIds);

    if (chunk !== null) {
      q = q.in("folder_id", chunk);
    }

    const { data, error } = await q;
    if (error) {
      if (params.throwOnError) throw new Error(`Filename search failed: ${error.message}`);
      // Keep the chunks already collected, the same way fetchDocumentsByIds
      // does: a caller that tolerates the failure merges these matches into a
      // list it already shows, so dropping every id gathered so far turns one
      // failed chunk into "no filename matches" for the whole subtree.
      console.warn("Filename search failed:", error.message);
      break;
    }
    ids.push(...(data ?? []).map((r: { id: number }) => r.id));
  }

  if (ids.length === 0) return { documents: [], truncated: false };

  // Each chunk is newest-first within itself, which says nothing about the
  // scope: sort the merged ids before cutting, so the rows that survive the cap
  // are the newest of every folder searched. A single chunk (the common case,
  // and the whole-project one) is already in this order — the sort is a no-op
  // there and the slice keeps its cap intact.
  ids.sort((a, b) => b - a);

  // Truncation is a property of the id search, NOT of the rows that come back:
  // fetchDocumentsByIds drops ids it must not show (deleted, enclosed in a
  // signed container), so counting rows reports a capped search as complete
  // whenever any id is dropped — and the caller's "first N matches" banner
  // exists precisely to stop a cut-off list reading as "that file isn't here".
  const truncated = ids.length > limit;

  return {
    documents: await fetchDocumentsByIds(ids.slice(0, limit), {
      throwOnError: params.throwOnError,
    }),
    truncated,
  };
}

/**
 * Tag-filtered subtree fetch for the /projects Folders tab.
 *
 * The shared page-level Filters control filters List/Timeline server-side
 * across the whole scope, but the Folders tab reads from a per-folder cache —
 * so a tag filter used to apply only to the open folder's rows and a match
 * sitting in a subfolder rendered as "no results". This returns every
 * eligible document in the project + folder subtree matching the selected
 * tag dimensions (AND across dimensions, OR within one — same semantics as
 * listDocuments) and, when `verifiedOnly` is set, carrying a Verified mark —
 * with or without a tag dimension. Row eligibility mirrors fetchProjectDocuments (the browse
 * list): latest version, not deleted, not hidden, no DWG children, no
 * container children. Container documents (incl. UNZIPPED) stay eligible —
 * the browse list keeps them too, rendering them as navigable
 * ContainerFolderRow entries.
 *
 * `folderIds` follows the searchProjectFilenameMatches contract: null = whole
 * project, [] = no rows (defensive), non-empty = restrict to those folders.
 */
/**
 * AND-intersect the latest document-id sets across every active tag dimension.
 * Returns [] when no dimension is active or the intersection is empty.
 */
async function intersectTaggedDocumentIds(params: {
  categoryIds?: number[];
  designDisciplineIds?: number[];
  designStageIds?: number[];
  scope: { buildingId: number; projectId?: number };
}): Promise<number[]> {
  const junctions: Array<[string, string, number[] | undefined]> = [
    ["document_document_categories", "category_id", params.categoryIds],
    ["document_design_disciplines", "discipline_id", params.designDisciplineIds],
    ["document_design_stages", "stage_id", params.designStageIds],
  ];
  const active = junctions.filter(
    (entry): entry is [string, string, number[]] => (entry[2]?.length ?? 0) > 0
  );

  // In parallel: the dimensions are independent, and each is itself a paged
  // sweep, so running them in sequence multiplied the wait by the number of
  // active filters — the more the user selected, the longer the view sat empty.
  const docIdSets = await Promise.all(
    active.map(
      async ([table, column, ids]) =>
        new Set<number>(await fetchAllM2MDocumentIds(table, column, ids, params.scope))
    )
  );
  if (docIdSets.length === 0) return [];

  // Intersect without an initial value so an empty intersection stays empty
  // (AND across dimensions).
  return Array.from(docIdSets.reduce((acc, set) => new Set([...acc].filter((id) => set.has(id)))));
}

/**
 * Ids per `.in()` filter. PostgREST puts the whole list in the request URL, so
 * an unbounded id filter fails the request (URL too long) once a selection or
 * a tag intersection grows past a few thousand rows.
 */
const IN_CHUNK_SIZE = 500;

/**
 * Query `documents` for the newest visible latest-version rows whose id is in
 * `candidateIds`, up to `limit`. PostgREST encodes `.in()` in the request URL,
 * so the whole intersection at once fails (URL too long) for a heavily-used
 * tag — the catch would then surface that as "no matches". Chunk the id filter
 * instead; `candidateIds` is pre-sorted descending so chunks are queried
 * newest-first, keeping the deterministic newest-survive-truncation cap.
 */
async function fetchVisibleLatestDocIds(params: {
  buildingId: string;
  projectId: number;
  folderIds: number[] | null;
  /** null = no tag dimension active (Verified-only fetch) — no id restriction. */
  candidateIds: number[] | null;
  verifiedOnly?: boolean;
  uploadDateRange?: UploadDateRange;
  limit: number;
}): Promise<number[]> {
  const { buildingId, projectId, folderIds, candidateIds, verifiedOnly, uploadDateRange, limit } =
    params;
  const ids: number[] = [];
  const chunks =
    candidateIds === null
      ? [null]
      : Array.from({ length: Math.ceil(candidateIds.length / IN_CHUNK_SIZE) }, (_, i) =>
          candidateIds.slice(i * IN_CHUNK_SIZE, (i + 1) * IN_CHUNK_SIZE)
        );

  for (const chunk of chunks) {
    if (ids.length >= limit) break;
    let q = supabase
      .from("documents")
      .select("id")
      .eq("building_id", Number(buildingId))
      .eq("project_id", projectId)
      .eq("is_deleted", false)
      .eq("is_latest_version", true)
      .eq("hidden", false)
      .is("parent_document_id", null)
      .or("is_dwg_child.is.null,is_dwg_child.eq.false")
      .order("id", { ascending: false })
      .limit(limit - ids.length);

    if (chunk !== null) {
      q = q.in("id", chunk);
    }
    if (folderIds !== null) {
      q = q.in("folder_id", folderIds);
    }
    // Verified is filtered here, not client-side after the cap — otherwise a
    // truncated fetch could drop verified rows that sit past the cap while
    // reporting them as "no matches".
    if (verifiedOnly) {
      q = q.not("verified_at", "is", null);
    }
    // Same reason: the added range is filtered before the cap, not after it.
    q = applyUploadDateRange(q, uploadDateRange);

    // Throw, don't swallow: the caller (ProjectScopedFolderView's tag-filter
    // effect) distinguishes "no matches" ([]) from failure (throw) — its catch
    // shows the loadFailed toast and falls back to the folder-local filter.
    const { data, error } = await q;
    if (error) throw new Error(error.message);

    ids.push(...(data ?? []).map((r: { id: number }) => r.id));
  }
  return ids;
}

export async function fetchProjectTagFilteredDocuments(params: {
  buildingId: string;
  projectId: number;
  folderIds: number[] | null;
  categoryIds?: number[];
  designDisciplineIds?: number[];
  designStageIds?: number[];
  verifiedOnly?: boolean;
  uploadDateRange?: UploadDateRange;
  limit?: number;
}): Promise<{ documents: DocumentRowMapped[]; truncated: boolean }> {
  if (params.folderIds !== null && params.folderIds.length === 0)
    return { documents: [], truncated: false };

  const hasTagDimension = [
    params.categoryIds,
    params.designDisciplineIds,
    params.designStageIds,
  ].some((ids) => ids?.length);
  const hasAddedRange = !!(params.uploadDateRange?.from || params.uploadDateRange?.to);
  if (!hasTagDimension && !params.verifiedOnly && !hasAddedRange) {
    return { documents: [], truncated: false };
  }

  // Verified-only (or an added range) with no tag dimension needs no junction
  // intersect — both predicates are applied directly in fetchVisibleLatestDocIds.
  let matchingIds: number[] | null = null;
  if (hasTagDimension) {
    matchingIds = await intersectTaggedDocumentIds({
      ...params,
      scope: { buildingId: Number(params.buildingId), projectId: params.projectId },
    });
    if (matchingIds.length === 0) return { documents: [], truncated: false };
    matchingIds.sort((a, b) => b - a);
  }

  const limit = params.limit ?? 500;

  // Fetch one id beyond the cap purely to detect truncation — exactly `limit`
  // matches with nothing left over is not a truncation. `truncated` is
  // surfaced to the user by the caller (toast in ProjectScopedFolderView).
  const ids = await fetchVisibleLatestDocIds({
    buildingId: params.buildingId,
    projectId: params.projectId,
    folderIds: params.folderIds,
    candidateIds: matchingIds,
    verifiedOnly: params.verifiedOnly,
    uploadDateRange: params.uploadDateRange,
    limit: limit + 1,
  });

  const truncated = ids.length > limit;
  if (truncated) ids.length = limit;

  if (ids.length === 0) return { documents: [], truncated };
  return { documents: await fetchDocumentsByIds(ids), truncated };
}

/**
 * Fetch documents by their IDs with full relations (same select as listDocuments).
 * Used to load content-matched documents not already in the filename results.
 *
 * Read paths that only display the result tolerate a failed fetch: they warn and
 * carry on with an empty list. A caller that ACTS on the result must pass
 * `{ throwOnError: true }` — "the query failed" and "these documents no longer
 * exist" are the same empty array otherwise, and reading a failure as the latter
 * is what lets a bulk trash report a live selection as missing and skip it.
 */
const FETCH_BY_IDS_SELECT = `
  id,
  filename,
  file_path,
  building_id,
  parent_document_id,
  content_type,
  size,
  created_at,
  original_created_at,
  modified_at,
  document_content_modified_date,
  effective_modified_at,
  created_by,
  state,
  version,
  version_group,
  source,
  container_type,
  project_id,
  folder_id,
  is_deleted,
  approved,
  verified_at,
  verified_by,
  subcategories,
  project_stage,
  user_profiles!documents_created_by_fk (
    first_name,
    last_name,
    avatar_url,
    color
  ),
  verified_by_profile:user_profiles!documents_verified_by_fk (
    first_name,
    last_name
  ),
  document_document_categories(
    document_categories(id, category_name, name_et)
  ),
  document_design_disciplines(
    design_disciplines(id, name, name_en, abbreviation, description)
  ),
  document_design_stages(
    design_stages(id, name, name_en, abbreviation)
  )
`;

export async function fetchDocumentsByIds(
  docIds: number[],
  options?: { throwOnError?: boolean }
): Promise<DocumentRowMapped[]> {
  if (docIds.length === 0) return [];

  // Chunk the id filter (see IN_CHUNK_SIZE): a bulk action resolves its whole
  // selection through here, and a folder checkbox cascades an entire subtree
  // into that selection — one `.in()` with every descendant id overflows the
  // request URL, which `throwOnError` callers surface as a failed action.
  const rows: DocumentRowMapped[] = [];
  for (let i = 0; i < docIds.length; i += IN_CHUNK_SIZE) {
    const { data, error } = await supabase
      .from("documents")
      .select(FETCH_BY_IDS_SELECT)
      .in("id", docIds.slice(i, i + IN_CHUNK_SIZE))
      .eq("is_deleted", false)
      .order("created_at", { ascending: false });

    if (error) {
      if (options?.throwOnError) {
        throw new Error(`Failed to load documents by id: ${error.message}`);
      }
      // Keep the chunks already fetched: a read-only caller (the timeline's
      // content matches) merges this result into what it displays, so throwing
      // the earlier rows away over one failed chunk empties the surface
      // instead of degrading it.
      console.warn("fetchDocumentsByIds failed:", error.message);
      break;
    }

    rows.push(...(data ?? []).map(mapDocumentRow));
  }

  // Each chunk comes back sorted on its own, which is not the same as the whole
  // result being sorted — re-sort so the newest-first contract holds across them.
  rows.sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""));

  // Callers resolve bare id lists (content-search matches, tag intersections)
  // that may name documents enclosed in a signed container — never rows in any
  // view. The search_documents_by_content RPC does not yet apply the
  // signed-container rule server-side (franklin-data-pipeline), so drop them
  // here where every id-list path converges.
  const parentIds = rows
    .map((row) => (row.parent_document_id == null ? null : Number(row.parent_document_id)))
    .filter((id): id is number => id !== null);
  if (parentIds.length === 0) return rows;

  const signedParents = await filterSignedContainerParentIds(parentIds);
  if (signedParents.size === 0) return rows;
  return rows.filter(
    (row) => row.parent_document_id == null || !signedParents.has(Number(row.parent_document_id))
  );
}

/**
 * Fetch every active (not soft-deleted) document inside a version group with
 * full relations — same join shape as `fetchDocumentsByIds` — ordered by
 * version descending (newest first). Used to populate the inline version
 * expansion in `DocumentListTable`.
 *
 * Throws on failure rather than handing back no rows: an empty chain is a
 * meaningful answer here (every predecessor hard-deleted), and `useVersionExpansion`
 * caches it as one — so a swallowed error would be cached as "no earlier versions"
 * and never retried.
 */
export async function fetchDocumentsInVersionGroup(
  versionGroup: string,
  excludeId?: number
): Promise<DocumentRowMapped[]> {
  let query = supabase
    .from("documents")
    .select(
      `
      id,
      filename,
      file_path,
      building_id,
      parent_document_id,
      content_type,
      size,
      created_at,
      original_created_at,
      modified_at,
      document_content_modified_date,
      effective_modified_at,
      created_by,
      state,
      version,
      version_group,
      source,
      container_type,
      project_id,
      folder_id,
      is_deleted,
      approved,
      verified_at,
      verified_by,
      subcategories,
      project_stage,
      user_profiles!documents_created_by_fk (
        first_name,
        last_name,
        avatar_url,
        color
      ),
      verified_by_profile:user_profiles!documents_verified_by_fk (
        first_name,
        last_name
      ),
      document_document_categories(
        document_categories(id, category_name, name_et)
      ),
      document_design_disciplines(
        design_disciplines(id, name, name_en, abbreviation, description)
      ),
      document_design_stages(
        design_stages(id, name, name_en, abbreviation)
      )
    `
    )
    .eq("version_group", versionGroup)
    .eq("is_deleted", false)
    .order("version", { ascending: false });

  if (excludeId !== undefined) {
    query = query.neq("id", excludeId);
  }

  const { data, error } = await query;

  if (error) throw new Error(error.message);

  return (data ?? []).map(mapDocumentRow);
}
